import { IApp } from "@/interfaces";
import {
  DIGITAL_HUMAN_LOADING,
  DIGITAL_HUMAN_SETTING,
  HYGEN_SESSION_ID,
  HYGEN_SESSION_INFO,
  INIT_DIGITAL_HUMAN,
  INIT_HYGEN_SESSION,
  INIT_HYGEN_SESSION_ERROR,
  LIVEAVATAR_SESSION,
  SESSION_INITIALIZED_SUCCESSFULLY,
  SPEAK_HYGEN_BOT,
  START_HYGEN_SESSION,
  STOP_HYGEN_SESSION
} from "@/store/modules/digital_human//constants";
import digital_human_setting from "@/utils/digital_human";
import { Commit } from "vuex";
import {
  InitHygenSessionPayload,
  StartHygenSessionPayload,
  SpeakHygenBotPayload,
  StopHygenSessionPayload,
  DigitalHumanState
} from "./interfaces";
import {
  create_liveavatar_session,
  create_liveavatar_session_token
} from "@/store/apis/hygen";

export default {
  [INIT_DIGITAL_HUMAN]: ({ commit }: { commit: Commit }): void => {
    const portal = process.env.VUE_APP_PORTAL ?? IApp.Portals.HCMS;
    const settings = digital_human_setting[portal];
    commit(DIGITAL_HUMAN_SETTING, settings);
  },
  [INIT_HYGEN_SESSION]: async (
    { commit }: { commit: Commit },
    payload: InitHygenSessionPayload
  ): Promise<any | null> => {
    try {
      commit(HYGEN_SESSION_ID, null);
      commit(HYGEN_SESSION_INFO, null);
      commit(LIVEAVATAR_SESSION, null);
      commit(INIT_HYGEN_SESSION_ERROR, null);
      commit(DIGITAL_HUMAN_LOADING, true);

      const tokenData = await create_liveavatar_session_token(payload);
      const session = create_liveavatar_session(tokenData.session_token);

      commit(HYGEN_SESSION_ID, tokenData.session_id);
      commit(LIVEAVATAR_SESSION, session);
      sessionStorage.setItem("hygen_session_id", tokenData.session_id);
      commit(DIGITAL_HUMAN_LOADING, false);

      return {
        session_id: tokenData.session_id,
        session_token: tokenData.session_token
      };
    } catch (e) {
      commit(DIGITAL_HUMAN_LOADING, false);
      const message =
        e instanceof Error ? e.message : "Failed to create LiveAvatar session";
      commit(INIT_HYGEN_SESSION_ERROR, message);
      return null;
    }
  },
  [START_HYGEN_SESSION]: async (
    { commit, state }: { commit: Commit; state: DigitalHumanState },
    _payload: StartHygenSessionPayload
  ): Promise<any | null> => {
    try {
      commit(INIT_HYGEN_SESSION_ERROR, null);
      commit(DIGITAL_HUMAN_LOADING, true);

      const session = state.liveavatar_session;
      if (!session) {
        throw new Error("LiveAvatar session is not initialized");
      }

      await session.start();
      commit(DIGITAL_HUMAN_LOADING, false);
      commit(SESSION_INITIALIZED_SUCCESSFULLY, true);
      return true;
    } catch (e) {
      commit(DIGITAL_HUMAN_LOADING, false);
      return null;
    }
  },
  [SPEAK_HYGEN_BOT]: async (
    { state }: { state: DigitalHumanState },
    payload: SpeakHygenBotPayload
  ): Promise<any | null> => {
    try {
      const session = state.liveavatar_session;
      if (!session) {
        return null;
      }
      const eventId = session.repeat(payload.text);
      return { event_id: eventId };
    } catch (e) {
      return null;
    }
  },
  [STOP_HYGEN_SESSION]: async (
    { commit, state }: { commit: Commit; state: DigitalHumanState },
    _payload: StopHygenSessionPayload
  ): Promise<any | null> => {
    try {
      const session = state.liveavatar_session;
      if (session) {
        await session.stop();
      }
      commit(LIVEAVATAR_SESSION, null);
      commit(HYGEN_SESSION_ID, null);
      commit(HYGEN_SESSION_INFO, null);
      commit(SESSION_INITIALIZED_SUCCESSFULLY, false);
      sessionStorage.removeItem("hygen_session_id");
      return true;
    } catch (e) {
      return null;
    }
  }
};
